import React, { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { BeatLoader } from 'react-spinners';

import styled from 'styled-components';

import { removeLogs } from '@libs/NwLogs';

import { useLayoutContext } from '@contexts/LayoutContext';
import { ListContext } from '@contexts/ListContext';
import { useStateValue } from '@contexts/StateProvider';

import NwNavBarAccountingMobile from '@components/_LayoutComponents/Header/NavBar/NwNavBarAccountingMobile';
import NwNavBarArtMobile from '@components/_LayoutComponents/Header/NavBar/NwNavBarArtMobile';
import NwNavBarBookingMobile from '@components/_LayoutComponents/Header/NavBar/NwNavBarBookingMobile';
import NwNavBarScoutingMobile from '@components/_LayoutComponents/Header/NavBar/NwNavBarScoutingMobile';
import { getNavLogs, redirectOnAreaChange } from '@components/_LayoutComponents/Header/NavBar/NwNavUtils';
import { HeaderContext } from '@components/_LayoutComponents/Header/NwHeader';

import ContactCreatorDrawer from '@components/ContactCreator/ContactCreatorDrawer';
import StyleVariables from '@components/Gui/StyleVariables';
import InvoiceDrawer from '@components/InvoicesAndCreditNotes/InvoiceDrawer';

import { NwNavIcon } from '../NwHeaderUI';
import NwNavBarAccounting from './NwNavBarAccounting';
import NwNavBarArt from './NwNavBarArt';
import NwNavBarBooking from './NwNavBarBooking';
import NwNavBarManagement from './NwNavBarManagement';
import NwNavBarScouting from './NwNavBarScouting';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const NoRecordLabel = styled.div`
    opacity: 0.75;
    font-size: 0.8rem;
    font-style: italic;
`;

const NwNavbarWrapper = ({ drawer }) => {
    const headerContextValues = useContext(HeaderContext);
    const { area, onCloseEventDrawer, closeNavDrawer } = headerContextValues;

    const layoutContext = useLayoutContext();
    const { isMobile } = layoutContext;
    const navigate = useNavigate();
    const [{ loadingStatusContext }] = useStateValue();
    const { cachedListLoaded } = useContext(ListContext);
    const [current, setCurrent] = useState('/test');
    const [creatorType, setCreatorType] = useState('Model');
    const [creatorDrawer, setCreatorDrawer] = useState(false);
    const [invoiceDrawer, setInvoiceDrawer] = useState(false);
    const [openKeys, setOpenKeys] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);

    const allLogs = getNavLogs(area);

    const scrollToTop = () => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    };

    useEffect(() => {
        setIsLoaded(loadingStatusContext.loaded);
    }, [loadingStatusContext]);

    const location = useLocation();
    const pageurl = location.pathname;

    useEffect(() => {
        const currentToSet = redirectOnAreaChange(allLogs, location, current, pageurl);
        if (currentToSet) {
            setCurrent(currentToSet);
        }
    }, [pageurl]);

    const closeCreatorDrawer = () => {
        setCreatorType();
        setCreatorDrawer(false);
    };

    const onCustomerCreated = (customer) => {
        if (area === 'booking' || area === 'art') {
            navigate(`/customer/${customer.ID}`);
        } else {
            navigate(`/${area}-customer/${customer.ID}`);
        }
        closeCreatorDrawer();
    };

    const onModelCreated = (ID) => {
        if (area === 'booking' || area === 'art') {
            navigate(`/model/${ID}`);
        } else {
            navigate(`/${area}-model/${ID}`);
        }
        closeCreatorDrawer();
    };

    const onProjectCreated = (ID) => {
        if (area === 'booking' || area === 'art') {
            navigate(`/bookings/${ID}`);
        } else {
            navigate(`/${area}-bookings/${ID}`);
        }
        closeCreatorDrawer();
    };

    const onTravelPlanCreated = (ID) => {
        navigate(`/scouting/travel/${ID}`);
        closeCreatorDrawer();
    };

    const onRemoveLogs = (logType) => {
        switch (logType) {
            case 'Model':
                removeLogs(`${area}_model`);
                break;
            case 'Customer':
                removeLogs(`${area}_customer`);
                break;
            case 'Project':
                removeLogs(`project`);
                break;
            case 'Trip':
                removeLogs(`travelPlan`);
                break;
            case 'Statement':
                removeLogs(`statement`);
                break;
            default:
                break;
        }
    };

    const handleClick = (e) => {
        if (!isLoaded) return;
        if (drawer) {
            closeNavDrawer();
        }
        if (e.key && !e.key.includes('menu') && !e.key.includes('no-key')) {
            if (e.key.includes('add')) {
                const creatortype = e.key.substr(4);
                setCreatorType(creatortype);
                setCreatorDrawer(true);
            } else if (e.key.includes('invoice-drawer')) {
                setInvoiceDrawer(Number(e.key.split('/')[2]));
            } else if (e.key.includes('remove')) {
                const removeLogType = e.key.substr(7);
                onRemoveLogs(removeLogType);
            } else {
                setCurrent(e.key);
                scrollToTop();
                navigate(`${e.key}`);
                setTimeout(() => {
                    scrollToTop();
                }, 0);
            }
        }
        setOpenKeys([]);
    };

    const createNewItemMenu = (key) => {
        if (!isLoaded) return;
        const items = [];
        if (key !== 'Statement') {
            items.push({
                key: `add-${key}`,
                label: (
                    <div className="nw-main-nav-subitems">
                        <NwNavIcon icon={byPrefixAndName.fass['plus-circle']} />
                        Create New
                    </div>
                ),
            });
        }
        if (!isMobile) {
            items.push({
                key: `remove-${key}`,
                label: (
                    <div className="nw-main-nav-subitems">
                        <NwNavIcon icon={byPrefixAndName.fass['trash-alt']} />
                        Clear History
                    </div>
                ),
            });
        }
        return {
            type: 'group',
            key: `/menu-actions-${key}`,
            label: (
                <span className="submenu-title-wrapper">
                    <NwNavIcon icon={byPrefixAndName.fasl['bolt']} />
                    Actions
                </span>
            ),
            children: items,
        };
    };

    const lastSeenMenu = (logs, prefix, noLabel, property) => {
        if (!isLoaded) return null;
        const items = [];
        if (logs) {
            logs.map((log) => {
                let logname = log.name;
                let logproperty = property ? log[property] : null;
                if (prefix === 'bookings') {
                    if (logproperty.toLowerCase() === logname.toLowerCase()) {
                        if (log.product) {
                            logname = log.product;
                        }
                    }
                }
                items.push({
                    key: `/${prefix}/${log.id}`,
                    label: logproperty ? (
                        <div className="last-seen-item">
                            <div>{logproperty}</div>
                            <small>{logname}</small>
                        </div>
                    ) : (
                        <>{logname}</>
                    ),
                });
                return null;
            });
        }
        if (!logs || logs.length === 0) {
            items.push({
                key: `no-key-${prefix}`,
                label: <NoRecordLabel>{noLabel}</NoRecordLabel>,
                disabled: true,
            });
        }
        return {
            type: 'group',
            key: `menu-last-seen-${prefix}`,
            label: (
                <span className="submenu-title-wrapper">
                    <NwNavIcon icon={byPrefixAndName.fasl['history']} />
                    Last Seen
                </span>
            ),
            children: items,
        };
    };

    const handleChangeOpenKeys = (keys, submenukeys) => {
        const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
        if (submenukeys.indexOf(latestOpenKey) === -1) {
            setOpenKeys(keys);
        } else {
            setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
        }
    };

    const isOpen = (key) => {
        return openKeys.find((openKey) => openKey === key);
    };

    const areaNavBarsProps = {
        allLogs,
        createNewItemMenu,
        current,
        drawer,
        handleChangeOpenKeys,
        handleClick,
        isLoaded,
        isOpen,
        lastSeenMenu,
        openKeys,
    };

    return (
        <>
            {cachedListLoaded ? (
                isMobile ? (
                    (() => {
                        switch (area) {
                            case 'accounting':
                                return <NwNavBarAccountingMobile {...areaNavBarsProps} />;
                            case 'art':
                                return <NwNavBarArtMobile {...areaNavBarsProps} />;
                            case 'scouting':
                                return <NwNavBarScoutingMobile {...areaNavBarsProps} />;
                            default:
                                return <NwNavBarBookingMobile {...areaNavBarsProps} />;
                        }
                    })()
                ) : (
                    (() => {
                        switch (area) {
                            case 'accounting':
                                return <NwNavBarAccounting {...areaNavBarsProps} />;
                            case 'art':
                                return <NwNavBarArt {...areaNavBarsProps} />;
                            case 'management':
                                return <NwNavBarManagement {...areaNavBarsProps} />;
                            case 'scouting':
                                return <NwNavBarScouting {...areaNavBarsProps} />;
                            default:
                                return <NwNavBarBooking {...areaNavBarsProps} />;
                        }
                    })()
                )
            ) : (
                <div style={{ height: 60, width: 200, textAlign: 'center', padding: '4px' }}>
                    <BeatLoader color={StyleVariables.PrimaryColor} size={8} />
                </div>
            )}
            {creatorDrawer && (
                <ContactCreatorDrawer
                    type={creatorType}
                    onCancel={closeCreatorDrawer}
                    onModelCreated={onModelCreated}
                    onCustomerCreated={onCustomerCreated}
                    onTravelPlanCreated={onTravelPlanCreated}
                    onProjectCreated={onProjectCreated}
                    onCloseEventDrawer={onCloseEventDrawer}
                />
            )}
            {invoiceDrawer && <InvoiceDrawer invoiceId={invoiceDrawer} onClose={() => setInvoiceDrawer()} />}
        </>
    );
};

export default NwNavbarWrapper;
